import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { Phone, Mail, MapPin } from "lucide-react";
import { BUSINESS } from "@/lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PROJECTS = ["Living", "Dining", "Bedroom", "Office", "Commercial", "Full home"];

const Field = ({ label, ...props }) => (
  <label className="block">
    <span className="eyebrow block mb-3">{label}</span>
    <input
      {...props}
      className="w-full bg-transparent border-b border-[#C9C2B7] py-3 text-lg focus:outline-none focus:border-[#1A1A1A] transition-colors duration-300 placeholder:text-[#B0A99E]"
    />
  </label>
);

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", project_type: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please add your name, email and a short message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Thank you — we'll be in touch within one business day.");
      setForm({ name: "", email: "", phone: "", project_type: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please call or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact" className="bg-[#1A1A1A] text-[#FBF9F6] py-24 md:py-40">
      <div className="max-w-[1500px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <Reveal>
            <span className="eyebrow">04 — Enquire</span>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-4 leading-[1.02]">
              Let's design something <span className="italic text-[#8C7A6B]">lasting.</span>
            </h2>
            <p className="text-[#B8B4AE] mt-6 max-w-md leading-relaxed">
              Visit the showroom, call, or leave a note below. Every enquiry is answered personally by our team.
            </p>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-12 space-y-6">
              <a data-testid="contact-phone" href={BUSINESS.phoneHref} className="flex items-center gap-4 group">
                <Phone size={18} className="text-[#8C7A6B]" />
                <span className="link-underline text-lg">{BUSINESS.phone}</span>
              </a>
              <a data-testid="contact-email" href={`mailto:${BUSINESS.email}`} className="flex items-center gap-4 group">
                <Mail size={18} className="text-[#8C7A6B]" />
                <span className="link-underline text-lg">{BUSINESS.email}</span>
              </a>
              <div className="flex items-center gap-4">
                <MapPin size={18} className="text-[#8C7A6B]" />
                <span className="text-lg text-[#B8B4AE]">{BUSINESS.address}</span>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <form data-testid="contact-form" onSubmit={submit} className="space-y-10">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                <Field label="Full name" data-testid="input-name" value={form.name} onChange={set("name")} placeholder="Jane Doe" />
                <Field label="Email" data-testid="input-email" type="email" value={form.email} onChange={set("email")} placeholder="jane@email.com" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                <Field label="Phone (optional)" data-testid="input-phone" value={form.phone} onChange={set("phone")} placeholder="+1 ..." />
                <label className="block">
                  <span className="eyebrow block mb-3">Project type</span>
                  <select
                    data-testid="input-project"
                    value={form.project_type}
                    onChange={set("project_type")}
                    className="w-full bg-transparent border-b border-[#C9C2B7] py-3 text-lg focus:outline-none focus:border-[#FBF9F6] transition-colors duration-300"
                  >
                    <option value="" className="bg-[#1A1A1A]">Select…</option>
                    {PROJECTS.map((p) => <option key={p} value={p} className="bg-[#1A1A1A]">{p}</option>)}
                  </select>
                </label>
              </div>
              <label className="block">
                <span className="eyebrow block mb-3">Tell us about your project</span>
                <textarea
                  data-testid="input-message"
                  value={form.message}
                  onChange={set("message")}
                  rows={4}
                  placeholder="Dimensions, timeline, materials, inspiration…"
                  className="w-full bg-transparent border-b border-[#C9C2B7] py-3 text-lg focus:outline-none focus:border-[#FBF9F6] transition-colors duration-300 resize-none placeholder:text-[#7d7770]"
                />
              </label>
              <button
                data-testid="contact-submit"
                type="submit"
                disabled={loading}
                className="bg-[#FBF9F6] text-[#1A1A1A] px-10 py-4 rounded-full text-sm tracking-wide hover:bg-[#8C7A6B] hover:text-white transition-colors duration-500 disabled:opacity-60"
              >
                {loading ? "Sending…" : "Send enquiry"}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
